import jsPDF from "jspdf";
import "jspdf-autotable";
import { formatDate, formatDateLocal, formatDateTimeLocal } from "./dateUtils";
import { convertRole, formatPhoneNumber } from "./textUtils";

// Placeholder for intentional line breaks while collapsing Word soft breaks.
const PDF_LINE_BREAK = "\uE000";

// Helper function to strip HTML tags and convert to plain text
const stripHtmlTags = (htmlString) => {
  if (!htmlString) return "";

  // Create a temporary div element to parse HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  // Convert <br> tags to line-break placeholders before getting text content
  tempDiv.querySelectorAll("br").forEach((br) =>
    br.replaceWith(PDF_LINE_BREAK),
  );

  // Preserve paragraph/block boundaries from Word-style HTML
  tempDiv.querySelectorAll("p, div").forEach((el) => {
    el.appendChild(document.createTextNode(PDF_LINE_BREAK));
  });

  // Process ordered lists
  tempDiv.querySelectorAll("ol").forEach((ol) => {
    let count = 1;
    Array.from(ol.children).forEach((child) => {
      if (child.tagName.toLowerCase() === "li") {
        const number = document.createTextNode(`${count}. `);
        child.insertBefore(number, child.firstChild);
        child.appendChild(document.createTextNode(PDF_LINE_BREAK));
        count++;
      }
    });
  });

  // Process unordered lists and restart logic for any remaining lis
  tempDiv.querySelectorAll("ul li").forEach((li) => {
    // Check if it already has a number (nested scenario edge case handling)
    if (!li.textContent.trim().match(/^\d+\./)) {
      const bullet = document.createTextNode("• ");
      li.insertBefore(bullet, li.firstChild);
      li.appendChild(document.createTextNode(PDF_LINE_BREAK));
    }
  });

  // Cleanup for any orphan lis or those not caught above (though querySelectorAll should be specific)
  // To be safe, we can target lis that are direct children of ul or outside list
  tempDiv.querySelectorAll("li").forEach((li) => {
    if (
      !li.textContent.includes("•") &&
      !li.textContent.trim().match(/^\d+\./)
    ) {
      const bullet = document.createTextNode("• ");
      li.insertBefore(bullet, li.firstChild);
      li.appendChild(document.createTextNode(PDF_LINE_BREAK));
    }
  });

  // Get text content (automatically strips HTML tags)
  let text = tempDiv.textContent || tempDiv.innerText || "";

  // Collapse Word/HTML soft line breaks and indentation to spaces
  text = text.replace(/\s+/g, " ");
  text = text.split(PDF_LINE_BREAK).join("\n");
  text = text.replace(/\n\s*\n/g, "\n").trim();

  return text;
};

/** First project company logo URL, or null. */
const getProjectLogoUrl = (project) => {
  const logos = project?.company_logo;
  if (!Array.isArray(logos) || logos.length === 0) return null;
  const url = logos[0]?.url;
  if (!url || !String(url).trim()) return null;
  return String(url).trim();
};

const resolveLogoImageFormat = (logo, dataUrl, mimeType = "") => {
  const filename = (logo?.filename || logo?.name || "").toLowerCase();
  if (
    filename.endsWith(".jpg") ||
    filename.endsWith(".jpeg") ||
    mimeType.includes("jpeg") ||
    (typeof dataUrl === "string" && dataUrl.startsWith("data:image/jpeg"))
  ) {
    return "JPEG";
  }
  return "PNG";
};

const dataUrlFromBlob = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const measureDataUrlImage = (dataUrl) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });

/**
 * Load project company logo for jsPDF. Returns null if missing or unloadable.
 * Failures are swallowed so PDF export still succeeds without a logo.
 */
const loadProjectLogoForPdf = async (project) => {
  const logo = Array.isArray(project?.company_logo)
    ? project.company_logo[0]
    : null;
  const url = getProjectLogoUrl(project);
  if (!url) return null;

  // Prefer fetch → data URL (works when S3/CORS allows reading the response).
  try {
    const response = await fetch(url, { mode: "cors" });
    if (response.ok) {
      const blob = await response.blob();
      const looksLikeImage =
        !blob.type ||
        blob.type.startsWith("image/") ||
        blob.type === "application/octet-stream";
      if (blob && looksLikeImage) {
        const dataUrl = await dataUrlFromBlob(blob);
        const dimensions = await measureDataUrlImage(dataUrl);
        if (dimensions?.width && dimensions?.height) {
          return {
            dataUrl,
            format: resolveLogoImageFormat(logo, dataUrl, blob.type || ""),
            width: dimensions.width,
            height: dimensions.height,
          };
        }
      }
    }
  } catch {
    // Fall through to Image() path
  }

  // Fallback: Image + canvas (also requires CORS; used if fetch path failed).
  try {
    const loaded = await new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = url;
    });
    if (!loaded?.naturalWidth || !loaded?.naturalHeight) return null;

    const canvas = document.createElement("canvas");
    canvas.width = loaded.naturalWidth;
    canvas.height = loaded.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(loaded, 0, 0);
    const dataUrl = canvas.toDataURL("image/png");
    return {
      dataUrl,
      format: "PNG",
      width: loaded.naturalWidth,
      height: loaded.naturalHeight,
    };
  } catch {
    return null;
  }
};

/** Place logo in the top-right of the first page header area. */
const addProjectLogoToPdf = (doc, logo) => {
  if (!logo?.dataUrl) return;

  const maxHeightMm = 14;
  const maxWidthMm = 45;
  const pageWidth = doc.internal.pageSize.getWidth();
  let heightMm = maxHeightMm;
  let widthMm = (logo.width / logo.height) * heightMm;
  if (widthMm > maxWidthMm) {
    widthMm = maxWidthMm;
    heightMm = (logo.height / logo.width) * widthMm;
  }

  const x = pageWidth - 14 - widthMm;
  const y = 8;
  try {
    doc.addImage(logo.dataUrl, logo.format || "PNG", x, y, widthMm, heightMm);
  } catch {
    // Continue without logo if jsPDF cannot encode the image
  }
};

const REPORT_DEFAULT_THEME = [16, 24, 65];
const REPORT_MARGIN_X = 14;
const REPORT_COMPACT_TOP = 20;
const REPORT_BOTTOM_MARGIN = 18;
const REPORT_DARK_TEXT = [33, 37, 41];
const REPORT_LONG_WORD_WIDTH_CAP = 40;
const REPORT_DENSE_LONG_WORD_WIDTH_CAP = 24;
const REPORT_COLORS = {
  line: [214, 218, 224],
  meta: [130, 136, 145],
  footer: [110, 116, 124],
  stripe: [248, 249, 251],
  label: [70, 74, 82],
  text: [0, 0, 0],
};

const getReportThemeColor = (project) => {
  const raw = project?.table_header_color;
  if (typeof raw !== "string") return [...REPORT_DEFAULT_THEME];
  const match = raw.match(
    /rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i,
  );
  if (!match) return [...REPORT_DEFAULT_THEME];
  return match.slice(1, 4).map((value) => Math.min(255, Number(value)));
};

const getReportLuminance = (rgb) => {
  const [r, g, b] = rgb.map((value) => {
    const channel = value / 255;
    return channel <= 0.03928
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const getReportOnThemeTextColor = (theme) => {
  const luminance = getReportLuminance(theme);
  const whiteContrast = 1.05 / (luminance + 0.05);
  const darkContrast =
    (luminance + 0.05) / (getReportLuminance(REPORT_DARK_TEXT) + 0.05);
  return whiteContrast >= darkContrast ? [255, 255, 255] : REPORT_DARK_TEXT;
};

const getReportAccentTextColor = (theme) =>
  1.05 / (getReportLuminance(theme) + 0.05) >= 3 ? theme : REPORT_DARK_TEXT;

const drawReportLogo = (doc, logo, x, y, maxWidth, maxHeight) => {
  if (!logo?.dataUrl || !logo.width || !logo.height) return 0;
  let width = maxWidth;
  let height = (logo.height / logo.width) * width;
  if (height > maxHeight) {
    height = maxHeight;
    width = (logo.width / logo.height) * height;
  }
  try {
    doc.addImage(
      logo.dataUrl,
      logo.format || "PNG",
      x,
      y,
      width,
      height,
      "report-project-logo",
    );
    return height;
  } catch {
    return 0;
  }
};

const drawReportHeader = (
  doc,
  { title, dateText, metaLabel, metaValue, logo, theme },
) => {
  const right = doc.internal.pageSize.getWidth() - REPORT_MARGIN_X;
  const logoHeight = drawReportLogo(doc, logo, REPORT_MARGIN_X, 10, 50, 16);
  const titleTop = logoHeight > 0 ? 10 + Math.max(logoHeight, 8) + 4 : 10;
  const baseline = titleTop + 6;
  const accent = getReportAccentTextColor(theme);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(...accent);
  doc.text(title, REPORT_MARGIN_X, baseline);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(dateText, right, baseline, { align: "right" });

  const ruleY = titleTop + 9;
  doc.setDrawColor(...theme);
  doc.setLineWidth(0.7);
  doc.line(REPORT_MARGIN_X, ruleY, right, ruleY);

  const metaY = ruleY + 6;
  const labelText = `${metaLabel} `;
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...REPORT_COLORS.label);
  doc.text(labelText, REPORT_MARGIN_X, metaY);
  const labelWidth = doc.getTextWidth(labelText);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...REPORT_COLORS.text);
  doc.text(String(metaValue), REPORT_MARGIN_X + labelWidth, metaY);

  doc.setLineWidth(0.2);
  return metaY + 4;
};

const drawReportPageChrome = (doc, { compactLabel, footerLeft, logo }) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const right = pageWidth - REPORT_MARGIN_X;
  const pageCount = doc.internal.getNumberOfPages();

  for (let page = 1; page <= pageCount; page++) {
    doc.setPage(page);
    if (page > 1) {
      drawReportLogo(doc, logo, REPORT_MARGIN_X, 8, 32, 8);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...REPORT_COLORS.meta);
      doc.text(compactLabel, right, 13.2, { align: "right" });
      doc.setDrawColor(...REPORT_COLORS.line);
      doc.setLineWidth(0.25);
      doc.line(REPORT_MARGIN_X, 16, right, 16);
    }

    doc.setDrawColor(...REPORT_COLORS.line);
    doc.setLineWidth(0.25);
    doc.line(REPORT_MARGIN_X, pageHeight - 14, right, pageHeight - 14);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...REPORT_COLORS.footer);
    const pageText = `Page ${page} of ${pageCount}`;
    const leftMaxWidth =
      right - REPORT_MARGIN_X - doc.getTextWidth(pageText) - 8;
    const leftLine =
      doc.splitTextToSize(String(footerLeft || ""), leftMaxWidth)[0] || "";
    doc.text(leftLine, REPORT_MARGIN_X, pageHeight - 8.5);
    doc.text(pageText, right, pageHeight - 8.5, { align: "right" });
  }

  doc.setLineWidth(0.2);
  doc.setTextColor(...REPORT_COLORS.text);
};

const splitReportCellLines = (text) => String(text).split(/\r\n|\r|\n/);

const measureReportCells = (doc, head, body, fontSize) => {
  doc.setFontSize(fontSize);
  doc.setFont("helvetica", "normal");
  const spaceWidth = doc.getTextWidth(" ");
  const toParagraphs = (text) =>
    splitReportCellLines(text).map((line) =>
      line
        .split(" ")
        .filter(Boolean)
        .map((word) => doc.getTextWidth(word)),
    );
  const longestWord = (paragraphs) => {
    let longest = 0;
    paragraphs.forEach((words) =>
      words.forEach((width) => {
        if (width > longest) longest = width;
      }),
    );
    return longest;
  };

  return head.map((header, columnIndex) => {
    doc.setFont("helvetica", "bold");
    const headerParagraphs = toParagraphs(header ?? "");
    const headerWordWidth = longestWord(headerParagraphs);

    doc.setFont("helvetica", "normal");
    let contentWidth = 0;
    let wordWidth = 0;
    const cells = body.map((row) => {
      const text = String(row[columnIndex] ?? "");
      splitReportCellLines(text).forEach((line) => {
        const lineWidth = doc.getTextWidth(line);
        if (lineWidth > contentWidth) contentWidth = lineWidth;
      });
      const paragraphs = toParagraphs(text);
      const cellWordWidth = longestWord(paragraphs);
      if (cellWordWidth > wordWidth) wordWidth = cellWordWidth;
      return paragraphs;
    });

    return {
      spaceWidth,
      headerWordWidth,
      contentWidth,
      wordWidth,
      rows: [headerParagraphs, ...cells],
    };
  });
};

const getReportColumnMetrics = (measuredColumns, { paddingX, longWordCap }) =>
  measuredColumns.map((column) => {
    const padding = paddingX * 2 + 0.5;
    const minWidth =
      Math.max(Math.min(column.wordWidth, longWordCap), column.headerWordWidth) +
      padding;
    const naturalWidth = Math.max(
      Math.max(column.contentWidth, column.headerWordWidth) + padding,
      minWidth,
    );
    return { ...column, padding, minWidth, naturalWidth };
  });

const countReportLines = (paragraphs, usableWidth, spaceWidth) => {
  let lines = 0;
  paragraphs.forEach((words) => {
    lines += 1;
    let current = 0;
    words.forEach((wordWidth) => {
      if (current > 0 && current + spaceWidth + wordWidth <= usableWidth + 1e-6) {
        current += spaceWidth + wordWidth;
        return;
      }
      if (current > 0) lines += 1;
      if (wordWidth > usableWidth) {
        const pieces = Math.ceil(wordWidth / usableWidth);
        lines += pieces - 1;
        current = wordWidth - (pieces - 1) * usableWidth;
      } else {
        current = wordWidth;
      }
    });
  });
  return Math.max(lines, 1);
};

const allocateReportColumnWidths = (metrics, availableWidth) => {
  const minTotal = metrics.reduce((sum, m) => sum + m.minWidth, 0);
  if (metrics.length === 0 || minTotal > availableWidth) return null;

  const power = 6;
  const tieBreak = 0.02;
  const step = 0.5;
  const lookahead = 24;
  const sampleSize = 400;
  const widths = metrics.map((m) => m.minWidth);
  const totalRows = metrics[0].rows.length;
  const rowIndexes =
    totalRows <= sampleSize + 1
      ? Array.from({ length: totalRows }, (_, row) => row)
      : [
          0,
          ...Array.from(
            { length: sampleSize },
            (_, index) => 1 + Math.floor((index * (totalRows - 1)) / sampleSize),
          ),
        ];
  const lineCount = (m, row, width) =>
    countReportLines(
      m.rows[rowIndexes[row]],
      Math.max(width - m.padding, 0.1),
      m.spaceWidth,
    );
  const rowCost = (powerSum, lineSum) =>
    powerSum ** (1 / power) + tieBreak * lineSum;

  const lines = metrics.map((m, column) =>
    rowIndexes.map((_, row) => lineCount(m, row, widths[column])),
  );
  const powerSums = rowIndexes.map((_, row) =>
    lines.reduce((sum, columnLines) => sum + columnLines[row] ** power, 0),
  );
  const lineSums = rowIndexes.map((_, row) =>
    lines.reduce((sum, columnLines) => sum + columnLines[row], 0),
  );

  let remaining = availableWidth - minTotal;
  while (remaining > 0.001) {
    let best = null;
    for (let column = 0; column < metrics.length; column++) {
      const m = metrics[column];
      const room = Math.min(remaining, m.naturalWidth - widths[column]);
      if (room <= 1e-6) continue;
      for (let k = 1; k <= lookahead; k++) {
        const added = Math.min(k * step, room);
        const width = widths[column] + added;
        let gain = 0;
        for (let row = 0; row < rowIndexes.length; row++) {
          const current = lines[column][row];
          const next = lineCount(m, row, width);
          if (next !== current) {
            gain +=
              rowCost(powerSums[row], lineSums[row]) -
              rowCost(
                powerSums[row] - current ** power + next ** power,
                lineSums[row] - current + next,
              );
          }
        }
        const gainPerMm = gain / added;
        if (gainPerMm > 1e-9 && (!best || gainPerMm > best.gainPerMm)) {
          best = { column, width, gainPerMm };
        }
        if (added >= room) break;
      }
    }
    if (!best) break;

    const m = metrics[best.column];
    remaining -= best.width - widths[best.column];
    widths[best.column] = best.width;
    for (let row = 0; row < rowIndexes.length; row++) {
      const current = lines[best.column][row];
      const next = lineCount(m, row, best.width);
      powerSums[row] += next ** power - current ** power;
      lineSums[row] += next - current;
      lines[best.column][row] = next;
    }
  }

  if (remaining > 0.001) {
    const growable = metrics
      .map((_, column) => column)
      .filter((column) => metrics[column].naturalWidth > widths[column] + 1e-6);
    const pool =
      growable.length > 0 ? growable : metrics.map((_, column) => column);
    const poolTotal = pool.reduce((sum, column) => sum + widths[column], 0);
    pool.forEach((column) => {
      widths[column] += (remaining * widths[column]) / poolTotal;
    });
  }

  return widths;
};

const buildReportTableOptions = (
  doc,
  { startY, theme, head, body, columnStyles = {} },
) => {
  const availableWidth = doc.internal.pageSize.getWidth() - REPORT_MARGIN_X * 2;
  const layouts = [
    {
      fontSize: head.length >= 13 ? 7.5 : 8,
      paddingX: 2,
      longWordCap: REPORT_LONG_WORD_WIDTH_CAP,
    },
    { fontSize: 7.5, paddingX: 1.3, longWordCap: REPORT_LONG_WORD_WIDTH_CAP },
    {
      fontSize: 7.5,
      paddingX: 0.9,
      longWordCap: REPORT_DENSE_LONG_WORD_WIDTH_CAP,
    },
  ];

  const measuredByFontSize = new Map();
  let layout = layouts[layouts.length - 1];
  let widths = null;
  for (const candidate of layouts) {
    if (!measuredByFontSize.has(candidate.fontSize)) {
      measuredByFontSize.set(
        candidate.fontSize,
        measureReportCells(doc, head, body, candidate.fontSize),
      );
    }
    widths = allocateReportColumnWidths(
      getReportColumnMetrics(
        measuredByFontSize.get(candidate.fontSize),
        candidate,
      ),
      availableWidth,
    );
    if (widths) {
      layout = candidate;
      break;
    }
  }

  const widthTotal = widths ? widths.reduce((sum, width) => sum + width, 0) : 0;
  const fittedColumnStyles = head.reduce((styles, _, index) => {
    const { cellWidth, ...rest } = columnStyles[index] || {};
    styles[index] =
      widthTotal > 0
        ? { ...rest, cellWidth: (widths[index] * availableWidth) / widthTotal }
        : rest;
    return styles;
  }, {});

  return {
    startY,
    theme: "plain",
    margin: {
      top: REPORT_COMPACT_TOP,
      bottom: REPORT_BOTTOM_MARGIN,
      left: REPORT_MARGIN_X,
      right: REPORT_MARGIN_X,
    },
    showHead: "everyPage",
    rowPageBreak: "auto",
    styles: {
      font: "helvetica",
      fontSize: layout.fontSize,
      cellPadding: {
        top: 1.8,
        right: layout.paddingX,
        bottom: 1.8,
        left: layout.paddingX,
      },
      overflow: "linebreak",
      valign: "middle",
      fillColor: [255, 255, 255],
      textColor: REPORT_COLORS.text,
      lineColor: REPORT_COLORS.line,
      lineWidth: { top: 0, right: 0, bottom: 0.2, left: 0 },
    },
    headStyles: {
      fillColor: theme,
      textColor: getReportOnThemeTextColor(theme),
      fontStyle: "bold",
      fontSize: layout.fontSize,
      valign: "middle",
      cellPadding: {
        top: 2.2,
        right: layout.paddingX,
        bottom: 2.2,
        left: layout.paddingX,
      },
      lineColor: theme,
      lineWidth: 0.1,
    },
    columnStyles: fittedColumnStyles,
    didParseCell: (data) => {
      if (data.section === "body" && data.row.index % 2 === 1) {
        data.cell.styles.fillColor = REPORT_COLORS.stripe;
      }
    },
    didDrawCell: (data) => {
      if (data.section === "body") {
        data.table.settings.rowPageBreak = "avoid";
      }
    },
  };
};

const getLegacySubmittalPdfKeys = (
  includeReleasedOn,
  includeEstimatedDeliveryDate,
) => {
  const keys = [
    "formatted_spec_section",
    "formatted_number",
    "revision",
    "title",
    "submitted_for",
    "formatted_received_from",
    "ball_in_court",
    "status",
    "review_status",
    "issue_date",
    "due_date",
  ];

  if (includeReleasedOn) {
    keys.push("released_on");
  }

  if (includeEstimatedDeliveryDate) {
    keys.push("estimated_delivery_date");
  }

  keys.push("most_recent_response_date");
  return keys;
};

const getSubmittalColumnDefinitions = () => ({
  formatted_spec_section: {
    header: "Spec Section",
    getValue: (submittal) => submittal.formatted_spec_section || "-",
  },
  formatted_number: {
    header: "Number",
    getValue: (submittal) => submittal.formatted_number || "-",
  },
  revision: {
    header: "Revision",
    getValue: (submittal) => submittal.revision || "0",
  },
  title: {
    header: "Title",
    getValue: (submittal) => submittal.title || "-",
    style: { cellWidth: 35 },
  },
  submitted_for: {
    header: "Submitted For",
    getValue: (submittal) => submittal.submitted_for || "For Approval",
  },
  formatted_received_from: {
    header: "From",
    getValue: (submittal) => submittal.formatted_received_from || "-",
  },
  ball_in_court: {
    header: "Ball in Court",
    getValue: (submittal) => submittal.formatted_ball_in_courts || "-",
    style: { cellWidth: 25 },
  },
  status: {
    header: "Status",
    getValue: (submittal) => submittal.status || "-",
  },
  review_status: {
    header: "Review Status",
    getValue: (submittal) => submittal.review_status || "-",
    style: { cellWidth: 20 },
  },
  issue_date: {
    header: "Issue Date",
    getValue: (submittal) =>
      submittal.issue_date
        ? formatDate(submittal.issue_date)
        : formatDateLocal(submittal.created_at),
    style: { cellWidth: 20 },
  },
  due_date: {
    header: "Due Date",
    getValue: (submittal) => formatDate(submittal.due_date),
    style: { cellWidth: 20 },
  },
  closed_at_date: {
    header: "Closed Date",
    getValue: (submittal) =>
      submittal.status === "Closed" && submittal.closed_at_date
        ? formatDate(submittal.closed_at_date)
        : "-",
    style: { cellWidth: 20 },
  },
  released_on: {
    header: "Released On",
    getValue: (submittal) => formatDate(submittal.released_on),
    style: { cellWidth: 20 },
  },
  estimated_delivery_date: {
    header: "Estimated Delivery Date",
    getValue: (submittal) => formatDate(submittal.estimated_delivery_date),
    style: { cellWidth: 26 },
  },
  most_recent_response_date: {
    header: "Most Recent Response Date",
    getValue: (submittal) => formatDateLocal(submittal.most_recent_response_date),
    style: { cellWidth: 20 },
  },
  priority: {
    header: "Priority",
    getValue: (submittal) => submittal.priority || "-",
  },
  edms: {
    header: "EDMS",
    getValue: (submittal) => submittal.edms || "-",
  },
});

export const exportSubmittalsToPDF = async (
  submittals,
  projectName,
  reportLabel = "Submittals",
  options = {}
) => {
  if (!submittals || submittals.length === 0) {
    throw new Error("No data to export");
  }

  const visibleColumns = Array.isArray(options.visibleColumns)
    ? options.visibleColumns
    : [];
  const includeReleasedOn = visibleColumns.includes("released_on");
  const includeEstimatedDeliveryDate = visibleColumns.includes(
    "estimated_delivery_date",
  );
  const columnOrder = Array.isArray(options.columnOrder)
    ? options.columnOrder.filter((column) => column !== "action")
    : [];
  const selectedProject = options.selectedProject || null;

  const legacyColumnKeys = getLegacySubmittalPdfKeys(
    includeReleasedOn,
    includeEstimatedDeliveryDate,
  );
  // most_recent_response_date was always included in the legacy fixed export
  // and is not restorable from the column selector for the PDF, so guarantee it
  // while honoring the user's visible-column selection for everything else.
  let orderedColumnKeys =
    columnOrder.length > 0 ? [...columnOrder] : [...legacyColumnKeys];
  if (
    columnOrder.length > 0 &&
    !orderedColumnKeys.includes("most_recent_response_date")
  ) {
    orderedColumnKeys.push("most_recent_response_date");
  }

  const columnDefinitions = getSubmittalColumnDefinitions();
  const selectedColumns = orderedColumnKeys
    .map((key) => columnDefinitions[key])
    .filter(Boolean);

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "letter",
  });

  const logo = await loadProjectLogoForPdf(selectedProject);
  const theme = getReportThemeColor(selectedProject);
  const reportTitle = `${reportLabel} Report`;
  const startY = drawReportHeader(doc, {
    title: reportTitle,
    dateText: `Generated Date: ${new Date().toLocaleDateString()}`,
    metaLabel: "Project Name:",
    metaValue: `${projectName}`,
    logo,
    theme,
  });

  const tableData = submittals.map((submittal) =>
    selectedColumns.map((column) => column.getValue(submittal) || "-"),
  );

  const columnStyles = selectedColumns.reduce((styles, column, index) => {
    if (column.style) {
      styles[index] = column.style;
    }
    return styles;
  }, {});

  const head = selectedColumns.map((column) => column.header);
  doc.autoTable({
    ...buildReportTableOptions(doc, {
      startY,
      theme,
      head,
      body: tableData,
      columnStyles,
    }),
    head: [head],
    body: tableData,
  });

  drawReportPageChrome(doc, {
    compactLabel: reportTitle,
    footerLeft: [projectName, reportTitle].filter(Boolean).join(" | "),
    logo,
  });

  const safeLabel = (reportLabel || "report").replace(" ", "-").toLowerCase();
  doc.save(`${safeLabel}-report-${new Date().toISOString().split("T")[0]}.pdf`);
};

const RFI_PDF_COLUMN_LABELS = {
  number: "Number",
  title: "Title",
  question: "Question",
  formatted_assignees: "Assignees",
  formatted_ball_in_court_users: "Ball in Court",
  formatted_distribution_list: "CCs",
  created_at: "Created",
  due_date: "Due Date",
  closed_at_date: "Closed Date",
  formatted_spec_section: "Spec Section",
  status: "Status",
  most_recent_response_date: "Most Recent Response Date",
};

export const getRFIPdfColumnOptions = (columnOrder = [], selectedKeys = []) => {
  const keys = columnOrder.filter((key) => RFI_PDF_COLUMN_LABELS[key]);
  if (!keys.includes("question")) {
    const titleIndex = keys.indexOf("title");
    keys.splice(titleIndex >= 0 ? titleIndex + 1 : keys.length, 0, "question");
  }
  if (!keys.includes("most_recent_response_date")) {
    keys.push("most_recent_response_date");
  }

  return keys.map((key) => ({
    key,
    label: RFI_PDF_COLUMN_LABELS[key],
    selected:
      selectedKeys.includes(key) ||
      key === "question" ||
      key === "most_recent_response_date",
  }));
};

export const exportRFIsToPDF = async (
  rfis,
  projectName = "Project",
  selectedProject = null,
  options = {},
) => {
  if (!rfis || rfis.length === 0) {
    throw new Error("No data to export");
  }

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "letter",
  });

  const logo = await loadProjectLogoForPdf(selectedProject);
  const theme = getReportThemeColor(selectedProject);
  const startY = drawReportHeader(doc, {
    title: "RFIs Report",
    dateText: `Generated Date: ${new Date().toLocaleDateString()}`,
    metaLabel: "Project:",
    metaValue: `${projectName || "N/A"}`,
    logo,
    theme,
  });
  const rfiPageChrome = {
    compactLabel: "RFIs Report",
    footerLeft: [projectName, "RFIs Report"].filter(Boolean).join(" | "),
    logo,
  };

  // Check project workflow type
  const isSequentialWorkflow =
    selectedProject?.rfi_workflow_type === "Sequential";

  const formatRFINumber = (number) => {
    if (number === null || number === undefined || number === "") return "-";
    return /^\d+$/.test(String(number))
      ? String(number).padStart(4, "0")
      : String(number);
  };

  const customColumns = Array.isArray(options.columns)
    ? options.columns.filter((column) => column !== "action")
    : [];
  const columnOrder = Array.isArray(options.columnOrder)
    ? options.columnOrder.filter((column) => column !== "action")
    : [];

  if (customColumns.length > 0) {
    const columnDefinitions = {
      number: {
        header: "Number",
        getValue: (rfi) => formatRFINumber(rfi.number),
      },
      title: {
        header: "Title",
        getValue: (rfi) => rfi.title || "-",
        style: { cellWidth: 35 },
      },
      formatted_assignees: {
        header: "Assignees",
        getValue: (rfi) =>
          rfi.formatted_assignees || formatAssignees(rfi.assignees),
        style: { cellWidth: 32 },
      },
      formatted_ball_in_court_users: {
        header: "Ball in Court",
        getValue: (rfi) =>
          rfi.formatted_ball_in_court_users ||
          formatBallInCourt(
            rfi.ball_in_court_users || rfi.ball_in_court_ids || [],
          ),
        style: { cellWidth: 32 },
      },
      formatted_distribution_list: {
        header: "CCs",
        getValue: (rfi) => rfi.formatted_distribution_list || "-",
        style: { cellWidth: 32 },
      },
      formatted_spec_section: {
        header: "Spec Section",
        getValue: (rfi) =>
          rfi.formatted_spec_section || rfi.spec_section?.label || "-",
        style: { cellWidth: 25 },
      },
      created_at: {
        header: "Created Date",
        getValue: (rfi) => formatDateLocal(rfi.created_at),
        style: { cellWidth: 20 },
      },
      sent_date: {
        header: "Sent Date",
        getValue: (rfi) =>
          formatDateLocal(rfi.sent_date || rfi.date_sent || rfi.created_at),
        style: { cellWidth: 20 },
      },
      due_date: {
        header: "Due Date",
        getValue: (rfi) => formatDate(rfi.due_date),
        style: { cellWidth: 20 },
      },
      closed_at_date: {
        header: "Closed Date",
        getValue: (rfi) =>
          rfi.status === "Closed" && rfi.closed_at_date
            ? formatDate(rfi.closed_at_date)
            : "-",
        style: { cellWidth: 20 },
      },
      status: {
        header: "Status",
        getValue: (rfi) => rfi.status || "-",
      },
      most_recent_response_date: {
        header: "Most Recent Response Date",
        getValue: (rfi) => formatDateLocal(rfi.most_recent_response_date),
        style: { cellWidth: 25 },
      },
    };

    // Constrain to the intended custom column SET. Use columnOrder only to
    // reorder WITHIN customColumns; never let UI visibility add or drop columns
    // (otherwise required columns like sent_date are lost and UI-only columns
    // such as created_at/formatted_assignees leak into the custom report).
    const customColumnSet = new Set(customColumns);
    const orderedFromUi = columnOrder.filter((key) => customColumnSet.has(key));
    const orderedSet = new Set(orderedFromUi);
    const missingCustom = customColumns.filter((key) => !orderedSet.has(key));
    const orderedCustomKeys = [...orderedFromUi, ...missingCustom];
    const selectedColumns = orderedCustomKeys
      .map((key) => columnDefinitions[key])
      .filter(Boolean);

    if (selectedColumns.length === 0) {
      throw new Error("No valid columns selected for export");
    }

    const tableData = rfis.map((rfi) =>
      selectedColumns.map((column) => column.getValue(rfi) || "-"),
    );

    const columnStyles = selectedColumns.reduce((styles, column, index) => {
      if (column.style) {
        styles[index] = column.style;
      }
      return styles;
    }, {});

    const head = selectedColumns.map((column) => column.header);
    doc.autoTable({
      ...buildReportTableOptions(doc, {
        startY,
        theme,
        head,
        body: tableData,
        columnStyles,
      }),
      head: [head],
      body: tableData,
    });

    drawReportPageChrome(doc, rfiPageChrome);

    const safeProjectName = (projectName || "project")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

    doc.save(
      `${safeProjectName}-rfis-${new Date().toISOString().split("T")[0]}.pdf`,
    );
    return;
  }

  const legacyRfiColumnKeys = [
    "number",
    "title",
    "question",
    ...(!isSequentialWorkflow ? ["formatted_assignees"] : []),
    "formatted_ball_in_court_users",
    "created_at",
    "due_date",
    "formatted_spec_section",
    "status",
    "most_recent_response_date",
  ];
  // "question" and "most_recent_response_date" are NOT user-selectable table
  // columns (they are absent from RFI_COLUMN_OPTIONS), but the RFI report must
  // always include them. Honor the user's visible-column selection/order for
  // everything else, then guarantee these two so they are never silently
  // dropped when a columnOrder (visibleColumns) is supplied.
  const exportColumns = Array.isArray(options.exportColumns)
    ? options.exportColumns.filter((column) => column !== "action")
    : null;
  let orderedRfiColumnKeys = exportColumns
    ? [...exportColumns]
    : columnOrder.length > 0
      ? [...columnOrder]
      : [...legacyRfiColumnKeys];
  if (!exportColumns && columnOrder.length > 0) {
    if (!orderedRfiColumnKeys.includes("question")) {
      const titleIndex = orderedRfiColumnKeys.indexOf("title");
      orderedRfiColumnKeys.splice(
        titleIndex >= 0 ? titleIndex + 1 : orderedRfiColumnKeys.length,
        0,
        "question",
      );
    }
    if (!orderedRfiColumnKeys.includes("most_recent_response_date")) {
      orderedRfiColumnKeys.push("most_recent_response_date");
    }
  }

  const legacyRfiColumnDefinitions = {
    number: {
      header: "Number",
      getValue: (rfi) => formatRFINumber(rfi.number),
    },
    title: {
      header: "Title",
      getValue: (rfi) => rfi.title || "-",
      style: { cellWidth: 25 },
    },
    question: {
      header: "Question",
      getValue: (rfi) => stripHtmlTags(rfi.question) || "-",
      style: { cellWidth: 35 },
    },
    formatted_assignees: {
      header: "Assignees",
      getValue: (rfi) => formatAssignees(rfi.assignees),
      style: { cellWidth: 25 },
    },
    formatted_ball_in_court_users: {
      header: "Ball in Court",
      getValue: (rfi) =>
        formatBallInCourt(
          rfi.ball_in_court_users || rfi.ball_in_court_ids || [],
        ),
      style: { cellWidth: 25 },
    },
    created_at: {
      header: "Created",
      getValue: (rfi) => formatDateLocal(rfi.created_at),
    },
    due_date: {
      header: "Due Date",
      getValue: (rfi) => formatDate(rfi.due_date),
    },
    closed_at_date: {
      header: "Closed Date",
      getValue: (rfi) =>
        rfi.status === "Closed" && rfi.closed_at_date
          ? formatDate(rfi.closed_at_date)
          : "-",
      style: { cellWidth: 20 },
    },
    formatted_spec_section: {
      header: "Spec Section",
      getValue: (rfi) =>
        rfi.formatted_spec_section || rfi.spec_section?.label || "-",
      style: { cellWidth: 25 },
    },
    status: {
      header: "Status",
      getValue: (rfi) => rfi.status || "-",
    },
    most_recent_response_date: {
      header: "Most Recent Response Date",
      getValue: (rfi) => formatDateLocal(rfi.most_recent_response_date),
      style: { cellWidth: 20 },
    },
  };

  const selectedLegacyColumns = orderedRfiColumnKeys
    .map((key) => legacyRfiColumnDefinitions[key])
    .filter(Boolean);

  if (selectedLegacyColumns.length === 0) {
    throw new Error("No valid columns selected for export");
  }

  const tableData = rfis.map((rfi) =>
    selectedLegacyColumns.map((column) => column.getValue(rfi) || "-"),
  );

  const columnStyles = selectedLegacyColumns.reduce((styles, column, index) => {
    if (column.style) {
      styles[index] = column.style;
    }
    return styles;
  }, {});

  const head = selectedLegacyColumns.map((column) => column.header);
  doc.autoTable({
    ...buildReportTableOptions(doc, {
      startY,
      theme,
      head,
      body: tableData,
      columnStyles,
    }),
    head: [head],
    body: tableData,
  });

  drawReportPageChrome(doc, rfiPageChrome);

  // Generate filename safely
  const safeProjectName = (projectName || "project")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  // Save the PDF
  doc.save(
    `${safeProjectName}-rfis-${new Date().toISOString().split("T")[0]}.pdf`,
  );
};

export const exportHealthReportToPDF = (projects) => {
  if (!projects || projects.length === 0) {
    throw new Error("No data to export");
  }

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "letter",
  });

  doc.setFontSize(16);
  doc.text("Health Report", 14, 15);

  doc.setFontSize(10);
  doc.text(`Generated Date: ${new Date().toLocaleDateString()}`, 14, 25);

  const headers = [
    "Project Name",
    "PM",
    "Total Submittals",
    "Open / Pending",
    "Open / R&R or Rejected",
    "Closed",
  ];

  const tableData = projects.map((project) => [
    project.project_name || "-",
    project.pm_name || "-",
    project.total_submittals ?? 0,
    project.open_pending_submittals ?? 0,
    project.open_revise_or_rejected_submittals ?? 0,
    project.closed_submittals ?? 0,
  ]);

  doc.autoTable({
    startY: 30,
    head: [headers],
    body: tableData,
    theme: "grid",
    styles: {
      fontSize: 8,
      cellPadding: 2,
      overflow: "linebreak",
    },
    columnStyles: {
      0: { cellWidth: 60 },
      1: { cellWidth: 40 },
      4: { cellWidth: 70 },
    },
    headStyles: {
      fillColor: [51, 51, 51],
      textColor: 255,
      fontSize: 8,
      fontStyle: "bold",
    },
  });

  doc.save(`health-report-${new Date().toISOString().split("T")[0]}.pdf`);
};

// Helper function to format assignees list
const formatAssignees = (assignees) => {
  if (!assignees || assignees.length === 0) return "-";
  return assignees
    .map(
      (assignee) =>
        assignee.username || `${assignee.first_name} ${assignee.last_name}`,
    )
    .join(", ");
};

// Helper function to format ball in court users
const formatBallInCourt = (ballInCourtUsers) => {
  if (!ballInCourtUsers || ballInCourtUsers.length === 0) return "-";

  // Handle both user objects and user IDs
  return ballInCourtUsers
    .map((user) => {
      if (typeof user === "object") {
        return (
          user.username ||
          `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
          user._id ||
          user.id
        );
      }
      return user; // If it's just an ID
    })
    .join(", ");
};

// Helper function to get current step number for sequential workflow
const getCurrentStepNumber = (rfi) => {
  if (!rfi?.steps || !rfi?.ball_in_court_ids || rfi.steps.length === 0) {
    return 0;
  }

  for (let i = 0; i < rfi.steps.length; i++) {
    const stepUserIds = rfi.steps[i].map((user) => user._id || user);
    const ballInCourtIds = rfi.ball_in_court_ids.map(
      (user) => user._id || user,
    );

    if (stepUserIds.some((id) => ballInCourtIds.includes(id))) {
      return i;
    }
  }
  return 0;
};

export const exportPunchListItemsToPDF = (
  punchListItems,
  projectName = "Project",
) => {
  if (!punchListItems || punchListItems.length === 0) {
    throw new Error("No data to export");
  }

  // Create landscape PDF
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "letter",
  });

  // Add title
  doc.setFontSize(16);
  doc.text("Punch List Items Report", 14, 15);

  // Add project info
  doc.setFontSize(10);
  doc.text(`Project: ${projectName || "N/A"}`, 14, 25);
  doc.text(`Generated Date: ${new Date().toLocaleDateString()}`, 14, 30);

  // Prepare table data
  const tableData = punchListItems.map((item) => [
    item.number || "-",
    item.title || "-",
    item.approver_name || "-",
    item.formatted_assignees || "-",
    item.status || "-",
    formatDateLocal(item.created_at),
    formatDateLocal(item.completed_at),
    item.location || "-",
    item.room || "-",
    item.notes || "-",
  ]);

  // Define table headers
  const headers = [
    "Number",
    "Title",
    "Approver",
    "Assignees",
    "Status",
    "Created Date",
    "Completed Date",
    "Location",
    "Room",
    "Notes",
  ];

  // Generate table
  doc.autoTable({
    startY: 35,
    head: [headers],
    body: tableData,
    theme: "grid",
    styles: {
      fontSize: 9,
      cellPadding: 3,
      overflow: "linebreak",
      halign: "left",
    },
    columnStyles: {
      0: { cellWidth: 20 }, // Number
      1: { cellWidth: 35 }, // Title
      2: { cellWidth: 30 }, // Approver
      3: { cellWidth: 35 }, // Assignees
      4: { cellWidth: 25 }, // Status
      5: { cellWidth: 25 }, // Created Date
      6: { cellWidth: 25 }, // Completed Date
      7: { cellWidth: 25 }, // Location
      8: { cellWidth: 20 }, // Room
      9: { cellWidth: 35 }, // Notes
    },
    headStyles: {
      fillColor: [51, 51, 51],
      textColor: 255,
      fontSize: 9,
      fontStyle: "bold",
    },
    margin: { right: 7 },
  });

  // Generate filename safely
  const safeProjectName = (projectName || "project")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  // Save the PDF
  doc.save(
    `${safeProjectName}-punch-list-items-${
      new Date().toISOString().split("T")[0]
    }.pdf`,
  );
};

export const exportSpecSectionsToPDF = (
  specSections,
  projectName = "Project",
) => {
  if (!specSections || specSections.length === 0) {
    throw new Error("No data to export");
  }

  const doc = new jsPDF();

  // Add title
  doc.setFontSize(16);
  doc.text("Specification Sections Report", 14, 15);

  // Add project info
  doc.setFontSize(10);
  doc.text(`Project: ${projectName || "N/A"}`, 14, 25);
  doc.text(`Generated Date: ${new Date().toLocaleDateString()}`, 14, 30);

  // Sort spec sections by section number (numeric-aware, matches API collation)
  const sortedSections = [...specSections].sort((a, b) =>
    String(a.section_number ?? "").localeCompare(
      String(b.section_number ?? ""),
      undefined,
      { numeric: true, sensitivity: "base" },
    ),
  );

  // Prepare table data
  const tableData = sortedSections.map((section) => [
    section.section_number || "-",
    section.title || "-",
    section.section_number && section.title
      ? `${section.section_number} - ${section.title}`
      : "-",
  ]);

  // Define table headers
  const headers = ["Section Number", "Title", "Full Reference"];

  // Generate table
  doc.autoTable({
    startY: 35,
    head: [headers],
    body: tableData,
    theme: "grid",
    styles: {
      fontSize: 10,
      cellPadding: 3,
      overflow: "linebreak",
    },
    columnStyles: {
      0: { cellWidth: 40 }, // Section Number
      1: { cellWidth: 70 }, // Title
      2: { cellWidth: 80 }, // Full Reference
    },
    headStyles: {
      fillColor: [51, 51, 51],
      textColor: 255,
      fontSize: 10,
      fontStyle: "bold",
    },
  });

  // Generate filename safely
  const safeProjectName = (projectName || "project")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  // Save the PDF
  doc.save(
    `${safeProjectName}-spec-sections-${
      new Date().toISOString().split("T")[0]
    }.pdf`,
  );
};

export const exportVendorsToPDF = (vendors, projectName = "Project", options = {}) => {
  const { preserveOrder = false } = options;
  if (!vendors || vendors.length === 0) {
    throw new Error("No data to export");
  }

  const doc = new jsPDF();

  // Add title
  doc.setFontSize(16);
  doc.text("Vendors Report", 14, 15);

  // Add project info
  doc.setFontSize(10);
  doc.text(`Project: ${projectName || "N/A"}`, 14, 25);
  doc.text(`Generated Date: ${new Date().toLocaleDateString()}`, 14, 30);

  // Sort vendors by name
  const sortedVendors = preserveOrder
    ? [...vendors]
    : [...vendors].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  // Prepare table data
  const tableData = sortedVendors.map((vendor) => [
    vendor.name || "-",
    vendor.dba || "-",
  ]);

  // Define table headers
  const headers = ["Name", "DBA"];

  // Generate table
  doc.autoTable({
    startY: 35,
    head: [headers],
    body: tableData,
    theme: "grid",
    styles: {
      fontSize: 10,
      cellPadding: 4,
      overflow: "linebreak",
    },
    columnStyles: {
      0: { cellWidth: 100 }, // Name
      1: { cellWidth: 80 }, // DBA
    },
    headStyles: {
      fillColor: [51, 51, 51],
      textColor: 255,
      fontSize: 10,
      fontStyle: "bold",
    },
  });

  // Generate filename safely
  const safeProjectName = (projectName || "project")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  // Save the PDF
  doc.save(
    `${safeProjectName}-vendors-${new Date().toISOString().split("T")[0]}.pdf`,
  );
};

export const exportUsersToPDF = (users, projectName = "Project", options = {}) => {
  const { preserveOrder = false } = options;
  const usersData = Array.isArray(users)
    ? users
    : Array.isArray(users?.data)
      ? users.data
      : [];

  if (usersData.length === 0) {
    throw new Error("No data to export");
  }

  // Create landscape PDF
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "letter",
  });

  // Add title
  doc.setFontSize(16);
  doc.text("Project Users Report", 14, 15);

  // Add project info
  doc.setFontSize(10);
  doc.text(`Project: ${projectName || "N/A"}`, 14, 25);
  doc.text(`Generated Date: ${new Date().toLocaleDateString()}`, 14, 30);

  // Sort users by role (project admin first) then by name
  const sortedUsers = preserveOrder
    ? [...usersData]
    : [...usersData].sort((a, b) => {
        if (a.role === "owner") return -1;
        if (b.role === "owner") return 1;
        return `${a.first_name} ${a.last_name}`.localeCompare(
          `${b.first_name} ${b.last_name}`,
        );
      });

  // Prepare table data
  const tableData = sortedUsers.map((user) => [
    user.first_name || "-",
    user.last_name || "-",
    user.email || "-",
    formatRole(user.role),
    user.vendor_name || "-",
    formatPhoneNumber(user.phone_number) || "-",
    user.notes || "-",
  ]);

  // Define table headers
  const headers = [
    "First Name",
    "Last Name",
    "Email",
    "Role",
    "Vendor",
    "Phone",
    "Notes",
  ];

  // Generate table with adjusted column widths for landscape mode
  doc.autoTable({
    startY: 35,
    head: [headers],
    body: tableData,
    theme: "grid",
    styles: {
      fontSize: 8,
      cellPadding: 3,
      overflow: "linebreak",
      halign: "left",
    },
    columnStyles: {
      0: { cellWidth: 24 }, // First Name
      1: { cellWidth: 24 }, // Last Name
      2: { cellWidth: 50 }, // Email
      3: { cellWidth: 28 }, // Role
      4: { cellWidth: 36 }, // Vendor
      5: { cellWidth: 30 }, // Phone
      6: { cellWidth: 55 }, // Notes
    },
    headStyles: {
      fillColor: [51, 51, 51],
      textColor: 255,
      fontSize: 8,
      fontStyle: "bold",
    },
    margin: { right: 7 },
  });

  // Generate filename safely
  const safeProjectName = (projectName || "project")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  // Save the PDF
  doc.save(
    `${safeProjectName}-users-${new Date().toISOString().split("T")[0]}.pdf`,
  );
};

// Helper function to format role
const formatRole = (role) => {
  if (!role) return "-";
  const label = convertRole(role);
  if (label) return label;
  return role
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Exports drawings to a PDF file
 * @param {Array} drawings - Array of drawing objects
 * @param {string} projectName - Name of the project
 * @param {string} folderName - Name of the drawing folder
 */
export const exportDrawingsToPDF = (drawings, projectName, folderName) => {
  // Create a new PDF document
  const doc = new jsPDF();

  // Add title
  doc.setFontSize(18);
  doc.text(`${projectName} - ${folderName}`, 14, 15);

  // Add subtitle
  doc.setFontSize(12);
  doc.text(`Drawing List - ${new Date().toLocaleDateString()}`, 14, 25);

  // Define the columns for the table
  const columns = [
    { header: "Drawing Number", dataKey: "drawing_number" },
    { header: "Title", dataKey: "title" },
    { header: "Uploaded", dataKey: "created_at" },
    { header: "Revision Date", dataKey: "revision_date" },
    { header: "Revision Title", dataKey: "revision_title" },
  ];

  // Format the data for the table
  const tableData = drawings.map((drawing) => ({
    ...drawing,
    revision_date: new Date(drawing.revision_date).toLocaleDateString(),
    created_at: drawing.created_at
      ? new Date(drawing.created_at).toLocaleDateString()
      : "",
  }));

  // Generate the table
  doc.autoTable({
    head: [columns.map((col) => col.header)],
    body: tableData.map((row) => columns.map((col) => row[col.dataKey])),
    startY: 35,
    theme: "grid",
    styles: { fontSize: 10 },
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: 255,
      fontStyle: "bold",
    },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  });

  // Add footer with page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: "center" },
    );
  }

  // Save the PDF
  doc.save(`${projectName}_${folderName}_drawings.pdf`);
};

function formatChangeOrderAssigneeForPdf(co) {
  const d = co.assignee_details;
  if (!d) return "";
  const name = `${d.first_name || ""} ${d.last_name || ""}`.trim();
  if (name) return name;
  if (d.username && String(d.username).trim()) return String(d.username).trim();
  if (d.company_name && String(d.company_name).trim()) return String(d.company_name).trim();
  return "";
}

function formatChangeOrderReviewAtForPdf(value) {
  if (!value) return "";
  const s =
    typeof value === "string"
      ? value
      : value instanceof Date
        ? value.toISOString()
        : String(value);
  return formatDateTimeLocal(s);
}

/**
 * Tabular PDF for project change orders (expects rows from
 * GET .../all-change-orders?is_export_pdf=true with assignee_details, most_recent_review_at).
 */
export const exportChangeOrdersToPDF = (changeOrders, projectName, options = {}) => {
  const { reportLabel = "Change Orders", includeDays = false, sections } = options;
  const tables = (sections || [{ title: null, rows: changeOrders }]).filter(
    (section) => section.rows?.length,
  );
  if (!tables.length) {
    throw new Error("No data to export");
  }

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(Number(value) || 0);

  const formatStatus = (s) => {
    if (!s) return "";
    const t = String(s);
    return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
  };

  const doc = new jsPDF({ orientation: "l", unit: "pt", format: "letter" });
  doc.setFontSize(16);
  doc.text(reportLabel, 40, 40);

  doc.setFontSize(10);
  doc.text(
    `Generated on: ${formatDateTimeLocal(new Date().toISOString())}`,
    40,
    58,
  );
  doc.text(`Project: ${projectName || ""}`, 40, 72);

  const head = [
    [
      "Number",
      "Title",
      "Contract Company",
      "Contract Title",
      "Status",
      "Assignee",
      "Invoiced Date",
      "Paid Date",
      "Amount",
      ...(includeDays ? ["Days"] : []),
      "Most Recent Review",
    ],
  ];

  const toBody = (rows) =>
    rows.map((r) => [
      r.number ?? "",
      String(r.title ?? ""),
      r.contract?.contract_company?.name ?? "",
      String(r.contract?.title ?? ""),
      formatStatus(r.status),
      formatChangeOrderAssigneeForPdf(r),
      formatDate(r.invoiced_date),
      formatDate(r.paid_date),
      formatCurrency(r.amount),
      ...(includeDays ? [r.days == null || r.days === "" ? "0" : String(r.days)] : []),
      formatChangeOrderReviewAtForPdf(r.most_recent_review_at),
    ]);

  const columnStyles = includeDays
    ? {
        0: { cellWidth: 44 },
        1: { cellWidth: 90 },
        2: { cellWidth: 88 },
        3: { cellWidth: 100 },
        4: { cellWidth: 50 },
        5: { cellWidth: 82 },
        6: { cellWidth: 58 },
        7: { cellWidth: 58 },
        8: { cellWidth: 56 },
        9: { cellWidth: 32 },
        10: { cellWidth: 78 },
      }
    : {
        0: { cellWidth: 44 },
        1: { cellWidth: 120 },
        2: { cellWidth: 88 },
        3: { cellWidth: 100 },
        4: { cellWidth: 50 },
        5: { cellWidth: 82 },
        6: { cellWidth: 58 },
        7: { cellWidth: 58 },
        8: { cellWidth: 56 },
        9: { cellWidth: 78 },
      };

  let startY = 88;
  tables.forEach((section, index) => {
    if (section.title) {
      if (index > 0) startY += 8;
      const pageH = doc.internal.pageSize.height;
      if (startY > pageH - 80) {
        doc.addPage();
        startY = 40;
      }
      doc.setFontSize(12);
      doc.text(section.title, 40, startY);
      startY += 14;
    }
    doc.autoTable({
      head,
      body: toBody(section.rows),
      startY,
      margin: { left: 28, right: 28 },
      styles: { fontSize: 7, cellPadding: 3, overflow: "linebreak" },
      headStyles: { fillColor: [71, 71, 71], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles,
    });
    startY = doc.lastAutoTable.finalY + 28;
  });

  const pageCount = doc.internal.getNumberOfPages();
  const pageW = doc.internal.pageSize.width;
  const pageH = doc.internal.pageSize.height;
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(`Page ${i} of ${pageCount}`, pageW / 2, pageH - 12, {
      align: "center",
    });
  }

  const safe = (projectName || "change-orders")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "_");
  doc.save(`${safe}_change_orders.pdf`);
};

export const exportContractstoPDF = (data, title, fileName, grandTotal, coLabelPlural = "Change Orders") => {
  const doc = new jsPDF("l", "pt"); // landscape orientation

  // Add title
  doc.setFontSize(16);
  doc.text(title, 40, 40);

  // Add timestamp
  doc.setFontSize(10);
  doc.text(
    `Generated on: ${formatDateTimeLocal(new Date().toISOString())}`,
    40,
    60,
  );

  // Convert data to format expected by autoTable
  const columns = Object.keys(data[0]).map((key) => ({
    header: key,
    dataKey: key,
  }));

  // Add main table
  const mainTable = doc.autoTable({
    columns,
    body: data,
    margin: { top: 80 },
    headStyles: { fillColor: [71, 71, 71] },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    styles: {
      fontSize: 8,
      cellPadding: 5,
    },
  });

  // Calculate position for totals table
  const finalY = mainTable.lastAutoTable.finalY + 20;

  // Format currency for totals
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value || 0);
  };

  // Create totals data
  const totalsData = [
    {
      Label: "Original Contract Amount",
      Amount: formatCurrency(grandTotal.original_contract_amount),
    },
    {
      Label: `Approved ${coLabelPlural}`,
      Amount: formatCurrency(grandTotal.approved_change_orders),
    },
    {
      Label: `Pending ${coLabelPlural}`,
      Amount: formatCurrency(grandTotal.pending_change_orders),
    },
    {
      Label: "Revised Contract Amount",
      Amount: formatCurrency(
        grandTotal.revised_contract_amount ??
          Number(grandTotal.original_contract_amount) +
            Number(grandTotal.approved_change_orders),
      ),
    },
  ];

  // Add totals table
  doc.autoTable({
    startY: finalY,
    head: [["Summary", "Amount"]],
    body: totalsData.map((item) => [item.Label, item.Amount]),
    headStyles: {
      fillColor: [71, 71, 71],
      fontSize: 10,
      fontStyle: "bold",
    },
    styles: {
      fontSize: 9,
      cellPadding: 5,
    },
    theme: "grid",
    margin: { left: 40 },
    tableWidth: 300,
  });

  // Save PDF
  doc.save(fileName);
};

export const exportTasksToPDF = async (
  tasks,
  projectName = "Project",
  selectedProject = null,
  options = {},
) => {
  if (!tasks || tasks.length === 0) {
    throw new Error("No data to export");
  }

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "letter",
  });

  const logo = await loadProjectLogoForPdf(selectedProject);
  addProjectLogoToPdf(doc, logo);

  doc.setFontSize(16);
  doc.text("Tasks Report", 14, 15);

  doc.setFontSize(10);
  doc.text(`Project: ${projectName || "N/A"}`, 14, 25);
  doc.text(`Generated Date: ${new Date().toLocaleDateString()}`, 14, 30);

  const formatTaskNumber = (number) => {
    if (number === null || number === undefined || number === "") return "-";
    return /^\d+$/.test(String(number))
      ? String(number).padStart(4, "0")
      : String(number);
  };

  const columnOrder = Array.isArray(options.columnOrder)
    ? options.columnOrder.filter((column) => column !== "action")
    : [];

  const defaultColumnKeys = [
    "number",
    "title",
    "formatted_ball_in_court_users",
    "priority",
    "created_at",
    "due_date",
    "status",
    "closed_at_date",
  ];

  // Relative widths — scaled to fill the landscape page (avoids a narrow table
  // when fewer columns are visible).
  const columnDefinitions = {
    number: {
      header: "Number",
      getValue: (task) => formatTaskNumber(task.number),
      weight: 1.2,
    },
    title: {
      header: "Title",
      getValue: (task) => task.title || "-",
      weight: 4.5,
    },
    formatted_ball_in_court_users: {
      header: "Ball in Court",
      getValue: (task) =>
        task.formatted_ball_in_court_users ||
        formatBallInCourt(
          task.ball_in_court_users || task.ball_in_court_ids || [],
        ),
      weight: 3.2,
    },
    priority: {
      header: "Priority",
      getValue: (task) => task.priority || "-",
      weight: 1.4,
    },
    created_at: {
      header: "Created Date",
      getValue: (task) => formatDateLocal(task.created_at),
      weight: 1.8,
    },
    due_date: {
      header: "Due Date",
      getValue: (task) => formatDate(task.due_date),
      weight: 1.8,
    },
    status: {
      header: "Status",
      getValue: (task) => task.status || "-",
      weight: 1.4,
    },
    closed_at_date: {
      header: "Closed Date",
      getValue: (task) =>
        task.status === "Closed" && task.closed_at_date
          ? formatDate(task.closed_at_date)
          : "-",
      weight: 1.8,
    },
  };

  const orderedKeys =
    columnOrder.length > 0
      ? columnOrder.filter((key) => columnDefinitions[key])
      : defaultColumnKeys;

  const selectedColumns = orderedKeys
    .map((key) => columnDefinitions[key])
    .filter(Boolean);

  if (selectedColumns.length === 0) {
    throw new Error("No valid columns selected for export");
  }

  const tableData = tasks.map((task) =>
    selectedColumns.map((column) => column.getValue(task) || "-"),
  );

  const marginLeft = 14;
  const marginRight = 14;
  const availableWidth =
    doc.internal.pageSize.getWidth() - marginLeft - marginRight;
  const totalWeight = selectedColumns.reduce(
    (sum, column) => sum + (column.weight || 1),
    0,
  );
  const columnStyles = selectedColumns.reduce((styles, column, index) => {
    styles[index] = {
      cellWidth: (availableWidth * (column.weight || 1)) / totalWeight,
    };
    return styles;
  }, {});

  doc.autoTable({
    startY: 35,
    head: [selectedColumns.map((column) => column.header)],
    body: tableData,
    theme: "grid",
    margin: { left: marginLeft, right: marginRight },
    tableWidth: availableWidth,
    styles: {
      fontSize: 8,
      cellPadding: 2,
      overflow: "linebreak",
    },
    columnStyles,
    headStyles: {
      fillColor: [51, 51, 51],
      textColor: 255,
      fontSize: 8,
      fontStyle: "bold",
    },
  });

  const safeProjectName = (projectName || "project")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  doc.save(
    `${safeProjectName}-tasks-${new Date().toISOString().split("T")[0]}.pdf`,
  );
};
